<template>
    <div class="e-card e-rounding shadow-sm">
        <PremiumBanner/>
        <h1 class="h3">{{ $t('title.salesItemsInfo') }}</h1>

        <Loader v-if="loading" />

        <div>
            <div class="d-flex flex-wrap-reverse justify-content-center justify-content-md-end mb-3">
                <button @click="$router.push('/import-export')" class="btn btn-outline-primary m-2">
                    <i class="fas fa-file-export pr-1"></i>{{ $t('items.exportImport') }}
                </button>
                <button @click="prepareAddItem()" class="btn btn-primary m-2">
                    <i class="fas fa-plus pr-1"></i>{{ $t('button.newItem') }}
                </button>
            </div>

            <div class="form-group" style="max-width: 400px;">
                <table><tr>
                    <td>
                        <vue-simple-suggest
                            id="search"
                            ref="queryRef"
                            v-model="query"
                            :debounce="2"
                            :placeholder="$t('search')"
                            :list="getSuggestionSaleAndSupplierList"
                            @input="queryChange"
                        >
                        </vue-simple-suggest>
                    </td>
                    <td style="padding: 10px"><i v-tooltip="$t('tooltip.barcodeScanner')" class="fa-solid fa-barcode fa-xl" :class="{'fa-beat':barcodeBeat}"></i></td>
 <!--                   <td>
                        <select v-if="!categoryViewEnabled" v-model="categoryFilter" class="form-control" @change="refresh()">
                            <option v-for="category in categories" v-bind:value="category.categoryId"
                                    v-bind:key="category.categoryId">
                                {{ category.path }}
                            </option>
                        </select>
                    </td>
//-->
                </tr></table>
            </div>

            <div class="col-12 mt-2 mb-2">
                <div class="d-flex flex-wrap">
                    <div class="custom-control custom-switch mr-3 mb-2">
                        <input type="checkbox" v-model="showNotSaleItems" @change="refresh()" class="custom-control-input"
                               role="switch" id="showNotSaleItems">
                        <label class="custom-control-label" for="showNotSaleItems">{{ $t('checkbox.showNotSaleItems') }}</label>
                    </div>
                      <div class="custom-control custom-switch mr-3 mb-2">
                        <input type="checkbox" v-model="categoryViewEnabled" @change="handleCategoryViewToggle"
                               class="custom-control-input" role="switch" id="categoryViewSwitch">
                        <label class="custom-control-label" for="categoryViewSwitch">{{ $t('categories.categoryViewLabel') }}</label>
                      </div>
                      <div class="custom-control custom-switch mb-2">
                        <input type="checkbox" v-model="showMarkupInsteadOfMargin" @change="handleMarginMarkupToggle"
                               class="custom-control-input" role="switch" id="showMarkupSwitch">
                        <label class="custom-control-label" for="showMarkupSwitch">{{ $t('checkbox.showMarkupInsteadOfMargin') }}</label>
                      </div>

                </div>
            </div>

            <div v-if="categoryViewEnabled">
                <div class="d-flex flex-wrap align-items-center mt-3 mb-4">
                    <template v-if="currentSelectedCategoryId !== null">
                        <button type="button" @click="navigateBackInCategoryView()" class="btn btn-outline-primary m-1">
                            <i class="fas fa-arrow-left mr-1"></i>
                            <span class="back-button-custom-text" v-if="currentSelectedCategoryId !== '*' && categoryMap.has(currentSelectedCategoryId) && categoryMap.get(currentSelectedCategoryId)">
                                <template v-if="parentOfCurrentCategory && parentOfCurrentCategory.path">
                                    {{ formatCategoryPathForDisplay(parentOfCurrentCategory.path) }}&nbsp;/&nbsp;
                                </template>
                                <strong>{{ categoryMap.get(currentSelectedCategoryId) ? categoryMap.get(currentSelectedCategoryId).name : '' }}</strong>
                            </span>
                        </button>
                    </template>

                    <template v-if="categoriesToDisplay.length > 0">
                        <button type="button" v-for="cat in categoriesToDisplay" :key="cat.categoryId"
                                @click="selectCategoryOnClick(cat)"
                                class="btn btn-outline-primary m-1"
                                :class="{'active': currentSelectedCategoryId === cat.categoryId}">
                            {{ cat.name }}
                        </button>
                    </template>

                    <button type="button" @click="selectAllCategories()"
                            class="btn btn-outline-primary m-1"
                            :class="{'active': currentSelectedCategoryId === '*'}">
                        {{ $t('categories.showAll') }}
                    </button>
                    
                    <div
                        v-if="!loading && (currentSelectedCategoryId === null || currentSelectedCategoryId === '*') && categoriesToDisplay.length === 0 && unifiedDisplayItems.length === 0"
                        class="alert alert-light w-100 mt-1">
                        {{ $t('categories.noTopLevelCategoriesOrItems') }}
                    </div>
                </div>
            </div>

            <div v-if="!loading">
                <table class="table table-hover e-table--responsive">
                    <thead>
                    <tr>
                        <th><Sort column-name="mcode" :number-type="true" :sort-data="sortData" @changeSort="changeSort($event)"> {{ $t('form.mcode') }} </Sort></th>
                        <th><Sort column-name="item" :number-type="false" :sort-data="sortData" @changeSort="changeSort($event)">{{ $t('form.item') }} </Sort> </th>
                        <th><Sort column-name="tax" :number-type="false" :sort-data="sortData" @changeSort="changeSort($event)">{{ $t('form.tax') }} </Sort></th>
                        <th class="price"><Sort column-name="price" :number-type="true" :sort-data="sortData" @changeSort="changeSort($event)">{{ $t('form.price')}} {{ currencyDisplay }} </Sort></th>
                        <th><Sort column-name="sku" :number-type="false" :sort-data="sortData" @changeSort="changeSort($event)">{{ $t('form.skuBarcode') }} </Sort></th>
                        <th><Sort column-name="category" :number-type="false" :sort-data="sortData" @changeSort="changeSort($event)">{{ $t('form.category') }} </Sort></th>
                        <th>{{ $t('form.supplier') }}</th>
                        <th><Sort :column-name="marginOrMarkupDisplay" :number-type="true" :sort-data="sortData" @changeSort="changeSort($event)">{{ marginOrMarkupDisplay === 'margin' ? $t('form.margin') : $t('form.markup') }} (%)</Sort></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="unifiedDisplayItems.length === 0">
                        <td :colspan="8" class="text-center"> <span v-if="categoryViewEnabled && currentSelectedCategoryId !== null && currentSelectedCategoryId !== '*'">{{ $t('messages.noItemsInCategory') }}</span>
                            <span v-else-if="categoryViewEnabled && currentSelectedCategoryId === null">{{ $t('messages.noRootItemsFound') }}</span>
                            <span v-else-if="categoryViewEnabled && currentSelectedCategoryId === '*'">{{ $t('messages.noItemsFound') }}</span>
                            <span v-else>{{ $t('messages.noItemsFound') }}</span> </td>
                    </tr>
                    <tr v-for="item in unifiedDisplayItems" :key="item.itemId">
                        <td :data-title="$t('form.mcode')">
                            <div v-if="item.onSale" class="e-mcode-square" @click="prepareEditItem(item.itemId)"
                                 v-bind:class="['m'+item.mcolor]">M{{item.mcode}}
                            </div>
                            <div v-if="!item.onSale" class="e-mcode-square notSale" @click="prepareEditItem(item.itemId)">M{{item.mcode}}
                            </div>
                        </td>
                        <td :data-title="$t('form.item')">
                            <span class="e-table-link" @click="prepareEditItem(item.itemId)"><Emphasize :value="item.text" :query="query"></Emphasize></span>
                        </td>
                        <td :data-title="$t('form.tax')">{{getTaxText(item.taxId)}}</td>
                        <td :data-title="$t('form.price')" v-if="item.priceStr !== ''" class="price"><Emphasize :value="item.priceStr" :query="query"></Emphasize></td>
                        <td :data-title="$t('form.price')" v-else class="price"><i>{{ $t('title.priceNotDefined')}}</i></td>
                        <td :data-title="$t('form.skuBarcode')"><Emphasize :value="item.sku" :query="query"></Emphasize></td>
                        <td :data-title="$t('form.category')"><Emphasize :value="getCategoryName(item.categoryId)" :query="query"></Emphasize></td>
                        <td :data-title="$t('form.supplier')"><Emphasize :value="item.supplier" :query="query"></Emphasize></td>
                        <td :data-title="marginOrMarkupDisplay === 'margin' ? $t('form.margin') : $t('form.markup')">{{ marginOrMarkupDisplay === 'margin' ? item.margin : item.markup }}</td>
                    </tr>
                    </tbody>
                </table>
              <Pagination :total="totalItems" :limit="limit" :offset="offset" @changePage="changePage($event)"></Pagination>


            </div>
        </div>
        <div><br></div>

        <div class="e-card-backdrop" v-if="showItem">
            <div class="row e-modal-row justify-content-center align-items-start">
                <div class="e-modal-card col-10 col-lg-5 bg-white shadow-sm e-rounding p-0">
                    <div class="card-header e-user__header" v-bind:class="['m'+item.mcolor]">
                        <span v-if="item.itemId === null">{{ $t('title.salesItemsNew') }}</span>
                        <span v-else>{{ $t('title.salesItemsEdit') }} <span>- M{{item.mcode}}</span></span>
                        <i @click="showItem = false" class="fas fa-times e-modal-close-icon"></i>
                    </div>
                    <ul class="nav nav-tabs" style="background-color: #cccccc">
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='item','elActive':navType==='item'}" aria-current="page" href="#" @click="navType='item'">{{ $t('item.submenu.sale')}}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='stock','elActive':navType==='stock'}" href="#" @click="navType='stock'">{{ $t('item.submenu.stock')}}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='modifiers','elActive':navType==='modifiers'}" href="#" @click="navType='modifiers'">{{ $t('item.submenu.modifiers')}}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='loyalty','elActive':navType==='loyalty'}" href="#" @click="navType='loyalty'">{{ $t('item.submenu.loyalty')}}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='other','elActive':navType==='other'}" href="#" @click="navType='other'">{{ $t('item.submenu.other')}}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='image','elActive':navType==='image'}" href="#" @click="navType='image'">{{ $t('item.submenu.image')}}</a>
                        </li>
                    </ul>
                    <div class="card-body py-5 px-4">
                        <div v-if="navType==='item'">
                            <div class="form-group">
                                <label for="mCode">{{ $t('form.mcode') }}</label> <i v-tooltip="$t('tooltip.mcode')" class="far fa-question-circle"></i>
                                <input v-model="item.mcodeStr" type="number" class="form-control" id="mCode">
                            </div>


                            <div class="form-group">
                              <label for="mColor">{{$t('title.selectColor') }}</label>
                              <div style="margin: auto">
                                <div style="overflow:hidden;" id="mColor">
                                  <div class="m1 mBox" v-bind:class="{mBoxSelected : item.mcolor===1}"
                                  @click="item.mcolor=1"/>
                                  <div class="m2 mBox" v-bind:class="{mBoxSelected : item.mcolor===2}"
                                  @click="item.mcolor=2"/>
                                  <div class="m3 mBox" v-bind:class="{mBoxSelected : item.mcolor===3}"
                                  @click="item.mcolor=3"/>
                                  <div class="m4 mBox" v-bind:class="{mBoxSelected : item.mcolor===4}"
                                  @click="item.mcolor=4"/>
                                  <div class="m5 mBox" v-bind:class="{mBoxSelected : item.mcolor===5}"
                                  @click="item.mcolor=5"/>
                                  <div class="m6 mBox" v-bind:class="{mBoxSelected : item.mcolor===6}"
                                  @click="item.mcolor=6"/>
                                  <div class="m7 mBox" v-bind:class="{mBoxSelected : item.mcolor===7}"
                                  @click="item.mcolor=7"/>
                                  <div class="m8 mBox" v-bind:class="{mBoxSelected : item.mcolor===8}"
                                  @click="item.mcolor=8"/>
                                </div>
                              </div>
                            </div>

                            <div class="form-group">
                                <label for="text">{{ $t('form.item') }}</label> <i v-tooltip="$t('tooltip.item')" class="far fa-question-circle"></i> <a v-show="!showEmoji" href="#" @click="showEmoji=true" style="color: #212529">+🎨</a>
                                <input ref="emojiTarget" v-model="item.text" class="form-control" id="text">
                                <Emoji v-if="showEmoji" @emoji="emojiSelected"></Emoji>
                            </div>
                            <div class="form-group">
                                <label for="tax">{{ $t('form.tax') }}</label> <i v-tooltip="$t('tooltip.tax')" class="far fa-question-circle"></i>
                                <select v-model="item.taxId" class="form-control" id="tax">
                                    <option v-for="t in taxEnabledItems" v-bind:value="t.taxId"
                                    v-bind:key="t.taxId">
                                    {{ t.name }} ({{ t.displayValue }})
                                    </option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label for="price">{{ $t('form.price')}} {{ currencyDisplay }}</label> <i v-tooltip="$t('tooltip.price')" class="far fa-question-circle"></i>
                                <input v-model="item.priceStr" class="form-control" id="price">
                            </div>
                            <div class="form-group">
                                <label for="sku">{{$t('form.skuBarcode') }}</label> <i v-tooltip="$t('tooltip.sku')" class="far fa-question-circle"></i>
                                <table><tr>
                                  <td><input v-model="item.sku" @change="refreshSku" class="form-control" id="sku" ref="itemSku" :class="{'border-success':barcodeBeat}"></td>
                                  <td style="padding: 10px"><i v-tooltip="$t('tooltip.barcodeScanner')" class="fa-solid fa-barcode fa-xl" :class="{'fa-beat':barcodeBeat}"></i>
                                    <span v-show="extractSkuFromWeightBarcode"> <a href="#" @click="extractSku()"> {{$t('item.extractSkuFromWeightBarcode')}}</a></span>
                                  </td>
                                </tr></table>
                            </div>
                            <div class="form-group">
                              <label for="category">{{$t('form.primaryCategory') }}</label> <i v-tooltip="$t('tooltip.primaryCategory')" class="far fa-question-circle"></i>
                              <select v-model="item.categoryId" class="form-control" id="category">
                                <option v-for="c in categories" v-bind:value="c.categoryId"
                                v-bind:key="c.categoryId">
                                  {{ c.name }}
                                </option>
                              </select>
                           </div>
                        </div>

                        <div  v-if="navType==='stock'">
                            <div>
                                <input type="radio" v-model="item.stockType" value="simple" class="btn-check" name="btnradio" id="btnradio1" autocomplete="off">
                                {{  $t('item.stockType.simple') }}
                                <i v-tooltip="$t('item.stockTypeToolTip.simple')" class="far fa-question-circle"></i>
                                <br>
                                <input type="radio" v-model="item.stockType" value="complex" class="btn-check" name="btnradio" id="btnradio2" autocomplete="off">
                                {{  $t('item.stockType.complex') }}
                                <i v-tooltip="$t('item.stockTypeToolTip.complex')" class="far fa-question-circle"></i>
                            </div>
                            <p></p>
                            <div v-if="item.stockType==='simple'">
                                <div v-if="item.sku===''" class="form-group" style="color: darkred">
                                    <i class="fa-solid fa-triangle-exclamation"></i> {{ $t('item.stock.skuMustBeSet')}}
                                </div>
                                <div class="form-group">
                                    <label for="purchasePrice">{{$t('form.purchasePrice') }} {{ currencyDisplay }}</label> <i v-tooltip="$t('tooltip.purchasePrice')" class="far fa-question-circle"></i>
                                    <input v-model="item.purchasePriceStr" class="form-control" id="purchasePrice">
                                </div>
                                <div class="form-group">
                                  <label for="lowQuantity">{{$t('form.lowQuantity') }}</label> <i v-tooltip="$t('tooltip.lowQuantity')" class="far fa-question-circle"></i>
                                  <input v-model="item.lowQuantityStr" class="form-control" id="lowQuantity">
                                </div>
                                <div class="form-group">
                                  <label for="supplier">{{$t('form.supplier') }}</label> <i v-tooltip="$t('tooltip.supplier')" class="far fa-question-circle"></i>
                                  <vue-simple-suggest
                                      id="supplier"
                                      v-model="item.supplier"
                                      :debounce="2"
                                      :list="getSuggestionSupplierList">
                                  </vue-simple-suggest>
                                </div>
                            </div>

                            <div v-if="item.stockType==='complex'">
                                <div v-if="showStockSuggestion" class="form-group">
                                    {{ $t('item.searchComplex') }}
                                    <vue-suggestion
                                        :items="skuSearchItems"
                                        :setLabel="skuSearchSetLabel"
                                        :itemTemplate="skuSearchTemplate"
                                        @changed="skuSearchChanged"
                                        @selected="skuSearchSelected"
                                    >
                                    </vue-suggestion>
                                </div>

                                <div v-if="!showStockSuggestion" class="form-group">
                                    <button @click="testStockItemExists();showStockSuggestion=true">
                                        + {{ $t('item.stock.connect') }}
                                    </button>
                                </div>
                                <div class="form-group">
                                    <div v-if="showNoStockWarning" class="form-group" style="color: darkred">
                                        <a href="/#/stock-items"><i class="fa-solid fa-triangle-exclamation"></i> {{ $t('warning.noStockItems')}}</a>
                                    </div>
                                    <table class="table table-hover e-table--responsive">
                                        <thead>
                                        <tr>
                                            <th>{{ $t('stock.table.sku') }}</th>
                                            <th>{{ $t('stock.table.name') }}</th>
                                            <th>{{ $t('stock.change') }}</th>
                                            <th>{{ $t('form.unit') }}</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="stock in item.stockList" v-bind:key="stock.sku">
                                            <td :data-title="$t('stock.table.sku')">{{ stock.sku }}</td>
                                            <td :data-title="$t('stock.table.name')">{{ stock.name }}</td>
                                            <td :data-title="$t('stock.change')"><input v-model="stock.quantityStr" class="form-control" :placeholder="$t('stock.change')"></td>
                                            <td :data-title="$t('form.unit')">
                                                <select v-model="stock.unitCode" class="form-control">
                                                    <option v-for="unit in stock.unitList" v-bind:value="unit.unitCode"
                                                            v-bind:key="unit.unitCode">
                                                        {{ unit.unitLong }}
                                                    </option>
                                                </select>
                                            </td>
                                            <td>
                                                <button @click="removeStockItem(stock.sku)">
                                                    -
                                                </button>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div  v-if="navType==='modifiers'">
                            <div>
                                <div style="margin: 10px;">
                                    {{ $t('modifier.infoApp') }}
                                </div>
                                <div v-if="showModifierSuggestion" class="form-group">
                                    {{ $t('item.searchModifier') }}
                                    <vue-suggestion
                                        :items="modifierSearchItems"
                                        :setLabel="modifierSearchSetLabel"
                                        :itemTemplate="modifierSearchTemplate"
                                        @changed="modifierSearchChanged"
                                        @selected="modifierSearchSelected"
                                    >
                                    </vue-suggestion>
                                </div>
                                <div v-if="!showModifierSuggestion" class="form-group">
                                    <button @click="showModifierSuggestion=true">
                                        + {{ $t('item.modifier.addModifier') }}
                                    </button>
                                </div>
                                <div class="form-group">
                                    <table class="table table-hover e-table--responsive">
                                        <thead>
                                        <tr>
                                            <th>{{ $t('form.mcode') }}</th>
                                            <th>{{ $t('form.item') }}</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="modifier in item.modifiers" v-bind:key="modifier.itemId">
                                            <td :data-title="$t('form.mcode')">
                                                <div class="e-mcode-square" v-bind:class="['m'+modifier.mcolor]">M{{modifier.mcode}}</div>
                                            </td>
                                            <td :data-title="$t('form.item')">{{ modifier.text }}</td>
                                            <td>
                                                <button @click="removeModifier(modifier.itemId)">
                                                    -
                                                </button>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div v-if="navType==='other'">
                          <div class="form-group">
                            <label for="note">{{$t('form.note') }}</label>
                            <input v-model="item.note" class="form-control" id="note">
                          </div>

                          <div style="color: #04084c">
                            {{ $t('item.other.appVersion') }}
                          </div>
                          <div class="form-group">
                            <label>{{$t('form.secondaryCategories') }}</label> <i v-tooltip="$t('tooltip.secondaryCategories')" class="far fa-question-circle"></i>
                            <div class="border rounded p-2" style="max-height: 220px; overflow-y: auto;">
                              <div v-for="c in removeEmptyCategory(categories)" :key="c.categoryId" class="custom-control custom-checkbox py-1">
                                <input type="checkbox" class="custom-control-input"
                                       :id="'sec-cat-' + c.categoryId"
                                       :value="c.categoryId"
                                       v-model="item.secondaryCategories">
                                <label class="custom-control-label" :for="'sec-cat-' + c.categoryId">{{ c.name }}</label>
                              </div>
                            </div>
                          </div>

                          <div class="form-group">
                            <label>{{ $t('barcode.title') }}</label>
                            <div>{{ $t('barcode.tip') }}</div>
                              <div v-for="(barcode, index) in item.barcodes" :key="index">
                                <div class="input-group mb-3" style="max-width: 400px;">
                                  <input  v-model="item.barcodes[index]" type="text" class="form-control mr-2">
                                  <button @click="item.barcodes.splice(index, 1);" type="button">-</button>
                                </div>
                              </div>
                              <div class="flex mt-4">
                                <button @click="item.barcodes.push('');">{{ $t('barcode.addButton') }}</button>
                                <i v-tooltip="$t('tooltip.barcodeScanner')" class="fa-solid fa-barcode fa-xl ml-1" :class="{'fa-beat':barcodeBeat}"></i>
                              </div>
                          </div>
                          <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" v-model="item.onSale" role="switch" id="onSale">
                            <label class="custom-control-label" for="onSale">{{$t('form.onSale') }}</label>
                          </div>

                        </div>

                        <div v-if="navType==='loyalty'">
                            <div><b>{{$t('loyalty.info')}}</b></div>
                            <div style="margin: 10px; color: #04084c">
                                {{ $t('loyalty.appVersion') }}
                            </div>
                            <div style="color: darkred" v-if="!loyaltyAllowed">
                                {{ $t('loyalty.business') }}
                            </div>
                            <div class="form-group">
                                <label for="loyaltyGain">{{$t('loyalty.form.gain') }}</label> <i v-tooltip="$t('loyalty.form.gain.info')" class="far fa-question-circle"></i>
                                <input v-model="item.loyaltySettings.gainPointsStr" class="form-control" id="loyaltyGain">
                            </div>
                            <div class="form-group">
                                <label for="loyaltyUse">{{$t('loyalty.form.use') }}</label> <i v-tooltip="$t('loyalty.form.use.info')" class="far fa-question-circle"></i>
                                <input v-model="item.loyaltySettings.usePointsStr" class="form-control" id="loyaltyUse">
                            </div>
                            <div class="form-group">
                                <label for="loyaltyDiscount">{{$t('loyalty.form.discount') }} %</label> <i v-tooltip="$t('loyalty.form.discount.info')" class="far fa-question-circle"></i>
                                <input v-model="item.loyaltySettings.discountStr" class="form-control" id="loyaltyDiscount">
                            </div>
                        </div>

                        <div v-if="navType==='image'">
                            <div v-if="item.itemId === null" class="alert alert-info">
                                {{ $t('item.image.saveFirst') }}
                            </div>
                            <div v-else>
                                <div v-if="item.imageId != null" class="mb-3">
                                    <img :src="bu + '/image/get-image?imageId=' + item.imageId + '&ak=' + ak" class="item-image-preview mb-2"/>
                                </div>
                                <p class="text-muted"><i class="fas fa-info-circle mr-1"></i>{{ $t('item.image.info') }}</p>
                                <input type="file" ref="itemImageFile" accept="image/png, image/jpeg" v-on:change="handleItemImageUpload()" style="display:none"/>
                                <ImageCropper ref="itemImageCropper"/>
                                <div>
                                    <button type="button" @click="$refs.itemImageFile.click()" class="btn btn-primary btn-sm mr-2" :disabled="uploadingImage">
                                        <i class="fa fa-upload pr-1"></i>{{ $t('item.image.upload') }}
                                    </button>
                                    <button v-if="item.imageId != null" type="button" @click="deleteItemImage()" class="btn btn-outline-danger btn-sm" :disabled="uploadingImage">
                                        <i class="fas fa-trash pr-1"></i>{{ $t('item.image.delete') }}
                                    </button>
                                    <i v-if="uploadingImage" class="fas fa-cog fa-spin ml-2"></i>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap-reverse justify-content-between pt-3">
                            <div>
                                <button v-if="item.itemId !== null && navType==='item'" @click="removeItem(item.itemId)" class="btn btn-danger mr-4">
                                    {{ $t('button.deleteItem')}}
                                </button>
                            </div>
                            <div class="e-btn-mobile-container">
                                <button @click="showItem = false" class="btn btn-outline-secondary mr-4">
                                    {{ $t('button.back')}}
                                </button>
                                <button v-if="item.itemId === null" @click="addItem()" class="btn btn-primary">
                                    {{ $t('button.createItem')}}
                                </button>
                                <button v-else @click="editItem()" class="btn btn-primary">
                                    {{ $t('button.save') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                    </div>
                    </div>
                    </div>
                    </template>

<script>
    import Loader from "@/components/Loader"
    import Sort from "@/components/Sort"
    import PremiumBanner from "@/components/PremiumBanner";
    import skuSearchTemplate from './template/sku-search-template';
    import modifierSearchTemplate from './template/modifier-search-template';
    import VueSimpleSuggest from 'vue-simple-suggest'
    import 'vue-simple-suggest/dist/styles.css'
    import Emphasize from "@/components/util/Emphasize.vue";
    import Emoji from "@/components/util/Emoji.vue";
    import Pagination from "@/components/util/Pagination.vue";
    import ImageCropper from "@/components/ImageCropper.vue";
    import {baseURL} from '@/config/variables'

    export default {
        name: 'Items',
        components: { Loader, Sort, PremiumBanner, VueSimpleSuggest, Emphasize, Emoji, Pagination, ImageCropper },

        data: function () {
            return {
                items: [],
                categories: [],
                item: {
                },
                taxItems: [],
                taxEnabledItems: [],
                showItem: false,
                loading: true,
                currencyDisplay: '',
                query: '',
                queryTimeout: null,
                queryInit : false,
                sortData: {
                    sortBy: 'mcode',
                    sortType: 'asc'
                },
                skuSearchItems: [],
                skuSearchItem: {},
                modifierSearchItems: [],
                skuSearchTemplate,
                modifierSearchTemplate,
                navType: 'item',
                showStockSuggestion: false,
                showModifierSuggestion: false,
                showNoStockWarning: false,
                barcodeBeat : false,
                //************************************************** */
                //   Toto už není už potřeba
                //************************************************** */
                // categoryFilter : null,
                extractSkuFromWeightBarcode : false,
                skuBeforeEdit : '',
                showNotSaleItems : false,
                marginOrMarkupDisplay: 'margin',
                loyaltyAllowed: false,
                showEmoji: false,
                categoryViewEnabled: false, //dafaulně vypnuto --> zobrazení všech položek
                currentSelectedCategoryId: null,
                limit: 50,
                offset: 0,
                totalItems: 0,
                bu: baseURL,
                ak: localStorage.apiKey,
                itemImageFile: '',
                uploadingImage: false
            }
        },
        computed: {
           categoriesToDisplay() {
    if (!this.categoryViewEnabled) return [];
    const validCategories = this.categories.filter(cat => cat.categoryId);

    if (this.currentSelectedCategoryId === null || this.currentSelectedCategoryId === '*') {
        // Top-level kategorie: path === name
        return validCategories.filter(cat => {
            if (!cat.path || !cat.name) return false;
            return cat.path === cat.name;
        });
    } else {
        const currentCategory = this.categoryMap.get(this.currentSelectedCategoryId);
        if (!currentCategory || !currentCategory.path) return [];

        // Podkategorie: path === parentPath + "/" + name
        return validCategories.filter(cat => {
            if (!cat.path || !cat.name || cat.categoryId === this.currentSelectedCategoryId) return false;
            const expectedPath = currentCategory.path + '/' + cat.name;
            return cat.path === expectedPath;
        });
    }
},
                unifiedDisplayItems() {
//************************************************** */
//   Toto už není už potřeba
//************************************************** */
//           if (this.categoryViewEnabled && this.currentSelectedCategoryId === null) {
//            return this.items.filter(item => !item.categoryId);
//        }
        return this.items;
    },


            parentOfCurrentCategory() {
                if (!this.currentSelectedCategoryId) {
                    return null;
                }
                const parent = this.getParentForCategory(this.currentSelectedCategoryId);
                return parent;
            },
            showMarkupInsteadOfMargin: {
                get() {
                    return this.marginOrMarkupDisplay === 'markup';
                },
                set(value) {
                    this.marginOrMarkupDisplay = value ? 'markup' : 'margin';
                }
            }
        },

        mounted: function () {
            this.readTaxItems();

            // Načíst preferenci z localStorage
            const savedDisplay = localStorage.getItem('itemsMarginOrMarkupDisplay');
            if (savedDisplay === 'margin' || savedDisplay === 'markup') {
                this.marginOrMarkupDisplay = savedDisplay;
            }

            const vm = this;
            vm.$root.$on('ws_message',(data)=>{
                if (data.type === 'clientEvent' && data.eventType === 'barcode') {
                    const value = JSON.parse(data.data).barcode;
                    let changed = false;
                    if(vm.showItem) {
                        if(vm.item.sku.length ===0 && vm.navType==='item') {
                            vm.item.sku = value;
                            changed = true;
                            vm.$refs.itemSku.focus();
                        } else if (vm.navType==='other') {
                            vm.item.barcodes.push(value);
                            changed = true;
                        }
                    } else {
                        vm.query = value;
                        changed = true;
                        vm.$refs.queryRef.input.focus();
                        vm.queryChange();
                    }
                    if(changed) {
                        vm.refreshSku();
                        vm.barcodeBeat = true;
                        setTimeout(function () {
                            vm.barcodeBeat = false;
                        }, 1000);
                    }
                }
            });

          //should we show item?
            if(typeof this.$route.query.id === 'string') {
                this.prepareEditItem(this.$route.query.id);
            }
        },
        beforeDestroy() {
            this.$root.$off("ws_message");
        },

 methods: {
    refresh: function(keepOffset) {
        const vm = this;
        vm.loading = true;
        vm.items = [];
        vm.totalItems = 0;
        if(!keepOffset) {
          vm.offset=0;
        }

        let categoryIdValue = null;
        let categoryRootValue = false;

        if (vm.categoryViewEnabled) {
            if (vm.currentSelectedCategoryId === null) {
                categoryRootValue = true;
            } else if (vm.currentSelectedCategoryId !== '*') {
                categoryIdValue = vm.currentSelectedCategoryId;
            }
        }

        this.$http.post('/item/get-items',{
            query: vm.query,
            categoryIdFilter: categoryIdValue,
            categoryRootFilter: categoryRootValue,
            sortData: vm.sortData,
            onlyOnSale: !vm.showNotSaleItems,
            limitAndOffset: {
              limit: vm.limit,
              offset: vm.offset
            }
        })
        .then(function (response) {
            vm.items = response.data.items;
            vm.totalItems = response.data.totalItems;
            vm.categories = response.data.categories;
            const emptyCategoryOption = vm.categories.find(c => c.categoryId === null);
            if (!emptyCategoryOption) {
                vm.categories.push({categoryId:null,name:'',path:''});
            }
            vm.crateCategoryMap();
            vm.loading = false;
            vm.currencyDisplay = response.data.currencyDisplay;
            vm.loyaltyAllowed = response.data.loyaltyAllowed;
            if (!vm.categoryViewEnabled) {
                vm.$nextTick(() => {
                    if (vm.$refs.queryRef && vm.$refs.queryRef.input) {
                        vm.$refs.queryRef.input.focus();
                    }
                });
            }
        });
    },
    removeEmptyCategory(categories) {
      return categories.filter(category => category.categoryId !== null);
    },
    selectAllCategories() {
        this.currentSelectedCategoryId = '*';
        this.refresh();    
},
            crateCategoryMap: function() {
                this.categoryMap = new Map();
                for (let i = 0; i < this.categories.length; i++) {
                    let category = this.categories[i];
                    this.categoryMap.set(category.categoryId,category);
                }
            },
             clearItem: function () {
                let preselectedCategoryId = null;
                if (this.categoryViewEnabled) {
                    preselectedCategoryId = this.currentSelectedCategoryId;
                } else {
                    preselectedCategoryId = this.categoryFilter;
                }
                this.item = {
                    itemId : null,
                    mcodeStr : "0",
                    text: "",
                    taxId: this.taxEnabledItems[0].taxId,
                    priceStr: "",
                    mcolor: 1,
                    sku: '',
                    categoryId: preselectedCategoryId,
                    stockList: [],
                    stockType: 'simple',
                    onSale: true,
                    loyaltySettings: {
                        gainPointsStr: "",
                        usePointsStr: "",
                        discountStr: ""
                    },
                    modifiers: [],
                    barcodes: [],
                    secondaryCategories: [],
                    imageId: null
                }
                this.showStockSuggestion=false;
                this.showNoStockWarning=false;
                this.extractSkuFromWeightBarcode=false;
            },
                        formatCategoryPathForDisplay(path) {
                if (path) {
                    return path.replace(/\//g, ' / ');
                }
                return '';
            },
            
            getParentForCategory(childCategoryId) {
                const childCat = this.categoryMap.get(childCategoryId);
                if (!childCat || !childCat.path || !childCat.name) {
                    return null;
                }
                // Pokud path === name, kategorie nemá rodiče
                if (childCat.path === childCat.name) {
                    return null;
                }
                // Cesta rodiče = aktuální path minus "/" + name na konci
                const suffix = '/' + childCat.name;
                if (!childCat.path.endsWith(suffix)) {
                    return null;
                }
                const parentPath = childCat.path.slice(0, -suffix.length);

                for (const category of this.categories) {
                    if (category.categoryId && category.path === parentPath) {
                        return category;
                    }
                }
                return null;
            },
            selectCategoryOnClick(category) {
                this.currentSelectedCategoryId = (category && category.categoryId) ? category.categoryId : null;
                this.refresh();
            },
navigateBackInCategoryView() {
    if (this.currentSelectedCategoryId === '*') {
        // Z "všechny kategorie" se vracíme na root (položky bez kategorie)
        this.selectCategoryOnClick(null); // currentSelectedCategoryId bude null
    } else if (this.parentOfCurrentCategory) {
        this.selectCategoryOnClick(this.parentOfCurrentCategory);
    } else if (this.currentSelectedCategoryId !== null) {
        // Pokud není rodič a nejsme na rootu (a ani ne '*' ), jdi na root
        this.selectCategoryOnClick(null);
    }
},
            handleCategoryViewToggle: function () {
                if (this.categoryViewEnabled) {
                    this.currentSelectedCategoryId = null;
                } else {
                    this.categoryFilter = null;
                    this.currentSelectedCategoryId = null;
                }
                this.refresh();
            },
            removeItem: function (itemId) {
                this.$modal.cs_confirm(this.$t('confirm.delete'),()=>{
                    const vm = this;
                    this.$http.post('/item/delete-item', {itemId: itemId})
                        .then(function () {
                            vm.showItem = false;
                            vm.refresh();
                        });
                });
            },
            prepareEditItem: function(itemId) {
                const vm = this;
                vm.navType = 'item';
                vm.showStockSuggestion = false;
                vm.showNoStockWarning = false;
                vm.itemImageFile = '';
                this.$http.post('/item/get-item',{uuid:itemId})
                    .then(function (response) {
                        vm.item = response.data;
                        if (!Array.isArray(vm.item.secondaryCategories)) {
                            vm.$set(vm.item, 'secondaryCategories', []);
                        }
                        vm.showItem = true;
                        vm.skuBeforeEdit = vm.item.sku;
                        vm.refreshSku();
                    });
            },
            prepareAddItem: function() {
                this.navType = 'item';
                this.itemImageFile = '';
                this.clearItem();

                const vm = this;
                vm.loading = true;
                this.$http.post('/item/get-items',{})
                    .then(function (response) {
                        vm.showItem = true;
                        vm.loading = false;

                        const allItems = response.data.items;
                        let code = 1;
                        while(code<10000) {
                            let ok = true;
                            for (let i = 0; i < allItems.length; i++) {
                                if (code === allItems[i].mcode) {
                                    ok = false;
                                    break;
                                }
                            }
                            if(ok === true) {
                                vm.item.mcodeStr = code;
                                break;
                            }
                            code++;
                        }
                    });

            },
            addItem: function() {
                const vm = this;
                this.$http.post('/item/add-item',this.item)
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.refresh();
                            vm.showItem = false;
                        }
                    });
            },
            editItem: function() {
                const vm = this;
                this.$http.post('/item/edit-item-fe',this.item)
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.refresh();
                            vm.showItem = false;
                        }
                    });
            },
            readTaxItems: function() {
                const vm = this;
                this.$http.post('/tax/get-taxes',{deleted:true})
                    .then(function (response) {
                        const taxes = response.data.taxes;
                        vm.taxItems = [];
                        vm.taxEnabledItems = [];
                        for(let i=0;i<taxes.length;i++) {
                            if(taxes[i].taxType !== 'TAX_EXEMPT') {
                                if (taxes[i].deleted === false) {
                                    vm.taxEnabledItems.push(taxes[i]);
                                }
                                vm.taxItems.push(taxes[i]);
                            }
                        }
                        vm.refresh();
                    });
            },
            getTaxText: function (taxId) {
                for (let i = 0; i < this.taxItems.length; i++) {
                    if(this.taxItems[i].taxId===taxId) {
                        return this.taxItems[i].name;
                    }
                }
            },
            getCategoryName: function(categoryId) {
                    return this.categoryMap.get(categoryId).name;
            },
            getCategoryColor: function(categoryId) {
                    return this.categoryMap.get(categoryId).color;
            },
            queryChange: function () {
                const vm = this;
              if(vm.queryInit) {
                if(this.queryTimeout !== null) {
                    clearTimeout(this.queryTimeout);
                }
                this.queryTimeout = setTimeout(function(){
                    vm.refresh();
                },500);
              } else {
                vm.queryInit = true;
              }
            },
            changeSort: function (sortData) {
                this.sortData = sortData;
                this.refresh();
            },
            handleMarginMarkupToggle: function() {
                // Uložit do localStorage
                localStorage.setItem('itemsMarginOrMarkupDisplay', this.marginOrMarkupDisplay);

                // Pokud je právě nastavené řazení podle margin/markup,
                // aktualizovat sortBy na novou hodnotu
                if (this.sortData.sortBy === 'margin' || this.sortData.sortBy === 'markup') {
                    this.sortData.sortBy = this.marginOrMarkupDisplay;
                }
                this.refresh();
            },
            changePage: function(pageData) {
                this.offset = pageData.offset;
                this.refresh(true);
            },
            skuSearchChanged :function(text) {
                const vm = this;
                this.$http.post('/stock-item/search-items', {query: text})
                    .then(function (response) {
                        vm.skuSearchItems = response.data.list;
                    });
            },
            skuSearchSelected: function(item) {
              //this.skuSearchItem = item;
              //insert a new line
                this.item.stockList.push({
                    sku: item.sku,
                    name: item.name,
                    unitList: item.unitList,
                    quantityStr: '',
                    unitCode: item.unitCode
                });
                this.showStockSuggestion = false;
            },
            skuSearchSetLabel : function() {
                return '';
            },
            removeStockItem: function(sku) {
                let index = -1;
                for(let i=0; i<this.item.stockList.length;i++) {
                    if(this.item.stockList[i].sku === sku) {
                        index = i;
                        break;
                    }
                }
                this.item.stockList.splice(index, 1);
            },
            testStockItemExists: function() {
                const vm = this;
                this.$http.post('/stock-item/stock-item-exist', {})
                    .then(function (response) {
                        vm.showNoStockWarning = !response.data.exists;
                    });
            },
            removeModifier: function(itemId) {
              const index = this.item.modifiers.findIndex(modifier => modifier.itemId === itemId);
              this.item.modifiers.splice(index,1);
            },
            modifierSearchChanged :function(text) {
                const vm = this;
                this.$http.post('/item/get-items', {query: text})
                    .then(function (response) {
                        vm.modifierSearchItems = response.data.items;
                    });
            },
            modifierSearchSelected: function(item) {
                //insert a new line
                this.item.modifiers.push({
                    itemId: item.itemId,
                    text: item.text,
                    mcode: item.mcode,
                    mcolor: item.mcolor,
                    mcodeStr: item.mcodeStr
                });
                this.showModifierSuggestion = false;
            },
            modifierSearchSetLabel : function() {
                return '';
            },
            refreshSku: function() {
                const vm = this;
                this.$http.post('/barcode/extract-sku-from-weight-barcode', {barcode: vm.item.sku})
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.extractSkuFromWeightBarcode = response.data.sku !== null;
                        }
                    });
            },
            extractSku: function() {
                const vm = this;
                this.$http.post('/barcode/extract-sku-from-weight-barcode', {barcode: vm.item.sku})
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.item.sku = response.data.sku;
                            vm.extractSkuFromWeightBarcode = false;
                        }
                    });
            },
            getSuggestionSupplierList :function(text) {
                const vm = this;
                return new Promise(function(resolve) {
                    vm.$http.post('/suggestion/search', {query: text, subjectType: 'supplier'})
                        .then(function (response) {
                            resolve(response.data.list);
                        });
                });
            },
            getSuggestionSaleAndSupplierList :function(text) {
                const vm = this;
                return new Promise(function(resolve) {
                    vm.$http.post('/suggestion/search', {query: text, subjectType: 'supplier', itemType: 'sale', category: true})
                        .then(function (response) {
                            resolve(response.data.list);
                        });
                });
            },
            emojiSelected: function (em) {
                this.showEmoji=false;
                let ti = this.$refs.emojiTarget;
                let startPos = ti.selectionStart,
                    endPos = ti.selectionEnd,
                    tmpStr = ti.value;

                this.item.text = tmpStr.substring(0, startPos) + em + tmpStr.substring(endPos, tmpStr.length);
            },
            handleItemImageUpload: function () {
                const file = this.$refs.itemImageFile.files[0];
                this.$refs.itemImageFile.value = '';
                if (!file) return;
                const vm = this;
                this.$refs.itemImageCropper.open(file).then(function (blob) {
                    if (!blob) return;
                    vm.uploadingImage = true;
                    const ext = blob.type === 'image/png' ? '.png' : '.jpg';
                    let formData = new FormData();
                    formData.append('file', blob, 'image' + ext);
                    vm.$http.post('/image/upload-item-image?itemId=' + vm.item.itemId, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then(function (response) {
                        vm.uploadingImage = false;
                        if (response.status === 200) {
                            vm.$http.post('/item/get-item', {uuid: vm.item.itemId})
                                .then(function (res) {
                                    vm.item = res.data;
                                });
                        }
                    }, function () {
                        vm.uploadingImage = false;
                    });
                });
            },
            deleteItemImage: function () {
                const vm = this;
                this.$modal.cs_confirm(this.$t('confirm.deleteItemImage'), () => {
                    let formData = new FormData();
                    this.$http.post('/image/upload-item-image?itemId=' + vm.item.itemId, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }).then(function (response) {
                        if (response.status === 200) {
                            vm.$http.post('/item/get-item', {uuid: vm.item.itemId})
                                .then(function (res) {
                                    vm.item = res.data;
                                });
                        }
                    });
                });
            }
        }
    }

</script>

<style>
    .m1 {
        background-color: #8ea459;
    }
    .m2 {
        background-color: #7ac4dd;
    }
    .m3 {
        background-color: #7aa1ec;
    }
    .m4 {
        background-color: #f0e071;
    }
    .m5 {
        background-color: #e49d5b;
    }
    .m6 {
        background-color: #b191dc;
    }
    .m7 {
        background-color: #d9736f;
    }
    .m8 {
        background-color: #ceb19f;
    }

    .elActive {
        font-weight: bold;
    }
    .elColor {
        color: black;
    }

    .mBox {
        display: inline-block;
        width: 30px;
        height: 30px;
        margin: 3px;
        border-radius: .25rem;
        cursor: pointer;
    }

    .e-mcode-square {
        width: max-content;
        padding: .5rem;
        border-radius: .25rem;
        font-weight: 600;
        text-align: center;
        cursor: pointer;
    }

    .mBoxSelected {
        border: 2px solid black;
    }

    .table td {
        vertical-align: middle;
        word-break: break-word
    }

    .price {
        text-align: right;
    }

    .notSale {
        text-decoration: line-through;
        background-color: #8a8a8a;
    }

    @media screen and (max-width: 576px) {
        .e-btn-mobile-container {
            width: 100%;
            text-align: right;
            margin-bottom: 2rem;
        }
    }

    .back-button-custom-text {
        color: black;
    }

    .item-image-preview {
        max-width: 100%;
        max-height: 300px;
        border: 1px solid #dee2e6;
        border-radius: .25rem;
        padding: 5px;
    }
</style>